<template>
  <div class="ym-player-container">
    <ym-mini-player :show="status === 'mini'"
                    @mini-player-click="handleMiniPlayerClick"
    />
    <div v-if="timeToShowPlayerBar && info.cover"
         class="player-action-bar"
         :class="{
          'is-tab-bar-page': isTabBarPage,
          'bar-fade-in': showPlayerBarByScroll,
          'bar-fade-out': !showPlayerBarByScroll,
         }"
         @click="handleShowPlayerDetail"
    >
      <div class="close-icon-container"
           @click.stop="handleCloseBar"
      >
        <ym-icon class="close-icon"
                 name="close"
        />
      </div>
      <ym-img class="cover"
              :src="info.cover"
              :alt="info.title"
      />
      <div class="info">
        <div class="title ym-line-ellipsis">{{ info.title }}</div>
        <div class="desc ym-line-ellipsis">{{ info.duration }} | 《{{ info.audioTitle }}》</div>
      </div>
      <div class="icon-container"
           @click.stop="handlePlay"
      >
        <ym-loading v-if="loading"
                    class="player-spinner"
                    type="fading-circle"
                    :size="50"
        />
        <ym-icon v-else
                 :name="icon"
        />
      </div>
    </div>
    <ym-popup v-model="showPlayerHintPopup"
              class="ym-player-hint-popup"
              position="top"
              get-container="body"
    >
      <div class="hint-popup-content">
        <div class="play-icon-container">
          <ym-img class="icon"
                  :src="require('@/assets/img/audio/bar-btn-playing.gif')"
                  alt="播放图标"
          />
        </div>
        <ym-img class="hint-arrow-icon"
                :src="require('@/assets/img/audio/player_hint_arrow.png')"
                alt="播放图标"
        />
        <span class="popup-hint-text">点这里可以继续<br />播放上一次的节目</span>
        <ym-tag class="popup-label"
                type="primary"
                size="medium"
                @click="handleClosePlayerBarHintPopup"
        >
          我知道了
        </ym-tag>
      </div>
    </ym-popup>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { Loading, Popup, Tag } from '@ymba/ym-ui';
  import PlayerPlaylistStorageMixin from '@/mixins/player/playerPlaylistStorage';
  import MiniPlayer from '@/components/Player/MiniPlayer';
  import { getFromStorage, getPlayerLastProgramIdStorageName, setToStorage } from '@/utils';
  import {
    PLAYER_POSITION_HINT_POPUP_SHOW,
    PLAYER_ACTION_BAR_CLOSE,
  } from '@/utils/constant';

  export default {
    name: 'YmPlayer',
    mixins: [PlayerPlaylistStorageMixin],
    components: {
      'ym-mini-player': MiniPlayer,
      'ym-loading': Loading,
      'ym-popup': Popup,
      'ym-tag': Tag,
    },
    props: {
      status: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        playlist: [],
        info: null,
        showPlayerBar: false,
        showPlayerBarByScroll: true,
        showPlayerHintPopup: false,
        isSetList: false,
      };
    },
    computed: {
      ...mapGetters([
        'player',
        'hidePlayerBar',
      ]),
      index() {
        return this.player.playlist.index;
      },
      list() {
        return this.player.playlist.list;
      },
      playingId() {
        return this.player.playingId;
      },
      playing() {
        return this.info && this.info.id === this.playingId;
      },
      icon() {
        return this.playing ? 'round-pause' : 'round-play';
      },
      loading() {
        return this.player.getLoadingStatus() === 'loading';
      },
      isTabBarPage() {
        return this.inTabBarPage();
      },
      timeToShowPlayerBar() {
        return !this.hidePlayerBar && this.status === 'bar' && this.showPlayerBar && this.info && this.showPlayerBarByScroll;
      },
    },
    watch: {
      playingId(val) {
        this.init();
        this.$store.dispatch('toggleHidePlayerBar', !(val > 0));
        this.showPlayerBarByScroll = val > 0;
      },
      $route(val) {
        const { name } = val;
        const pageMap = [
          'Home',
          'Audio',
          'Course',
          'Mine',
          'Learning',
        ];
        this.showPlayerBar = Boolean(pageMap.indexOf(name) > -1);
      },
    },
    methods: {
      handlePlay() {
        const { player } = this;
        // 正在播放则暂停
        if (this.playingId > 0) {
          player.pause();
          return;
        }
        // 进行播放
        const index = player.getPlaylistIndexById(this.info.id);
        player.play(index);
      },
      fetchScroll() {
        const { pageYOffset } = window;
        const { documentElement } = document;
        const scrollTop = pageYOffset || documentElement.scrollTop || document.body.scrollTop;
        const scroll = scrollTop - this.i;
        this.i = scrollTop;
        this.showPlayerBarByScroll = scroll < 0;
      },
      handleShowPlayerDetail() {
        const route = { name: 'AudioProgramDetail', params: { id: this.info.id } };
        this.$router.push(route);
      },
      handleCloseBar() {
        // 设置vuex
        this.$store.dispatch('toggleHidePlayerBar', true);
        // 是否已关闭过
        // 是否在一级页面
        // 是否在播音频
        const storage = getFromStorage(PLAYER_POSITION_HINT_POPUP_SHOW);
        this.showPlayerBar = false;
        if (storage) return;
        if (this.isPageWithMiniPlayer()) {
          setToStorage(PLAYER_POSITION_HINT_POPUP_SHOW, true);
          window.scrollTo(0, 0);
          this.showPlayerHintPopup = true;
        }
        setToStorage(PLAYER_ACTION_BAR_CLOSE, true);
      },
      handleClosePlayerBarHintPopup() {
        this.showPlayerBar = false;
        this.showPlayerHintPopup = false;
      },
      inTabBarPage() {
        const pageNames = ['Home', 'Audio', 'Course', 'Learning', 'Mine'];
        const { $route } = this;
        const { name } = $route;
        return pageNames.find(item => item === name);
      },
      isPageWithMiniPlayer() {
        // 当前页面是否包含mini player
        const pageNames = ['Home', 'Audio', 'Course'];
        const { $route } = this;
        const { name } = $route;
        return pageNames.find(item => item === name);
      },
      handleMiniPlayerClick() {
        // 设置vuex
        this.$store.dispatch('toggleHidePlayerBar', false);

        const list = this.getGlobalPlaylistStorage();
        if (!list || list.length === 0) {
          this.$toast('暂无可播放资源');
          return;
        }
        this.setGlobalPlayerInfo(list, true, true, 'program');
        const lastProgramId = getFromStorage(getPlayerLastProgramIdStorageName());
        const id = this.playingId > 0 ? this.playingId : lastProgramId;
        const route = { name: 'AudioProgramDetail', params: { id } };
        this.$router.push(route);
      },
      initPlayerInfo(index) {
        this.info = this.playlist[index];
      },
      init() {
        const list = this.getGlobalPlaylistStorage();
        if (!list || list.length === 0) return;

        this.playlist = list;

        this.setGlobalPlayerInfo(this.playlist);

        const programId = Number(getFromStorage(getPlayerLastProgramIdStorageName())) || 0;
        const index = list.findIndex(item => item.id === programId);

        this.initPlayerInfo(index);
      },
    },
    created() {
      this.init();
    },
    mounted() {
      window.addEventListener('scroll', this.fetchScroll, true);
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .ym-player-container {
    position: relative;
    z-index: @z-index-extra-large;
  }

  @keyframes barFadeIn {
    0% {
      opacity: 0;
      visibility: hidden;
    }
    100% {
      opacity: 1;
      visibility: visible;
    }
  }

  @keyframes barFadeOut {
    0% {
      opacity: 1;
      visibility: visible;
    }
    50% {
      opacity: .5;
    }
    100% {
      opacity: 0;
      visibility: hidden;
    }
  }

  .player-action-bar {
    position: fixed;
    bottom: 140px;
    left: 50%;
    width: 710px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: @size-small @size-base @size-small @size-small;
    box-sizing: border-box;
    background: fade(@color-text-light, 95%);
    box-shadow: 0 2px 7px 0 fade(@color-black, 10%);
    border-radius: @border-radius-normal * 2;
    transform: translateX(-50%);

    &.is-tab-bar-page {
      bottom: 200px;
    }

    &.bar-fade-in {
      animation: barFadeIn 1s ease-in;
    }

    &.bar-fade-out {
      animation: barFadeOut 1s ease-out;
    }
  }

  .cover {
    margin-right: @size-extra-small;
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    border-radius: @border-radius-normal;
    /deep/ img {
      border-radius: @border-radius-normal;
    }
  }

  .info {
    width: 420px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    color: @color-white;
  }

  .title {
    width: 100%;
    margin-bottom: @size-extra-small + @size-extra-small / 2;
    font-size: @font-size-base;
    line-height: 1;
  }

  .desc {
    width: 100%;
    color: @color-gray-lighter;
    font-size: @font-size-extra-small;
    line-height: 1.2;
  }

  @icon-size: 80px;
  .icon-container {
    min-width: @icon-size;
    height: @icon-size;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: @icon-size;
    color: @color-white;
  }

  .close-icon-container {
    width: @size-base;
    height: 80px;
    margin-right: @size-small;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    .close-icon {
      color: @color-white;
      font-size: @font-size-extra-small;
    }
  }

  .ym-player-hint-popup {
    background: none;
  }

  .hint-popup-content {
    padding: @size-base @size-extra-small;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
  }

  .play-icon-container {
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: @color-white;
    .icon {
      width: @size-medium;
      height: @size-medium;
    }
  }

  .hint-arrow-icon {
    margin-right: 104px;
    width: 114px;
    height: 148px;
  }

  .popup-hint-text {
    margin-right: 120px;
    color: @color-white;
    text-align: center;
    font-size: @font-size-medium;
  }

  .popup-label {
    margin: @size-base 160px 0 0;
  }


</style>
